import { BOARD_HEIGHT, DIVIDER_HEIGHT } from "../../../gearshiftGame/constants";
import {
  isLaneLabelAgentAsset,
} from "../../../lib/utils";

/**
 * Adds task_metadata to the first TASK trial in the mission timeline
 * @param {Array} timeline The mission timeline array
 * @param {Array} trials The trials config array
 * @param {Object} metadata The metadata to add
 */
export function addFirstTaskMetadata(timeline, trials, metadata) {
  const firstTaskIndex = trials.findIndex((trial) => trial.trialType === "TASK");

  if (firstTaskIndex !== -1 && timeline[firstTaskIndex]) {
    const firstTaskTrial = timeline[firstTaskIndex];

    // If it's a nested timeline, add metadata to the first trial in its timeline
    if (firstTaskTrial.timeline && firstTaskTrial.timeline[0]) {
      firstTaskTrial.timeline[0].data = {
        ...firstTaskTrial.timeline[0]?.data,
        task_metadata: metadata,
      };
    } else {
      // Otherwise add directly to the trial's data
      firstTaskTrial.data = {
        ...firstTaskTrial.data,
        task_metadata: metadata,
      };
    }
  }
}

// Helper functions for managing the preserved frame in tutorials

// Get the font family used in the experiment for consistent typography in preserved frame
export function getExperimentFontFamily() {
  const experimentWindow = document.getElementById("experiment-window");
  if (experimentWindow) {
    return window.getComputedStyle(experimentWindow).fontFamily;
  }
  return window.getComputedStyle(document.body).fontFamily;
}

// Creates a preserved frame overlay with consistent typography and necessary visual elements for the tutorial board preview
export function applyPreservedFrameTypography(preserved, display) {
  preserved.style.fontFamily = getExperimentFontFamily();
  if (!display || display === document.body) return;

  // Preserve jsPsych typography context (line-height/size rules often depend on this container)
  if (display.classList && display.classList.contains("jspsych-display-element")) {
    preserved.classList.add("jspsych-display-element");
  }

  const computed = window.getComputedStyle(display);
  if (computed.fontSize) preserved.style.fontSize = computed.fontSize;
  if (computed.lineHeight) preserved.style.lineHeight = computed.lineHeight;
  if (computed.letterSpacing) preserved.style.letterSpacing = computed.letterSpacing;
}

// Ensures that key visual elements (like the road dividers and lives text) are present in the preserved frame
export function ensurePreservedFrameVisuals(preserved, score) {
  let road = preserved.querySelector("#allHighwayDividers");
  if (!road) {
    const gameBoard = preserved.querySelector("#gameBoard");
    if (gameBoard) {
      road = document.createElement("div");
      road.id = "allHighwayDividers";
      gameBoard.appendChild(road);
    }
  }

  if (road && road.children.length === 0) {
    const dividerLefts = [60, 125, 190, 255, 320, 385];
    const dividerSpacing = DIVIDER_HEIGHT * 2;
    for (let top = 0; top < BOARD_HEIGHT; top += dividerSpacing) {
      const height = Math.min(DIVIDER_HEIGHT, BOARD_HEIGHT - top);
      dividerLefts.forEach((left) => {
        const divider = document.createElement("div");
        divider.classList.add("highwayDivider");
        divider.style.top = `${top}px`;
        divider.style.left = `${left}px`;
        divider.style.height = `${height}px`;
        road.appendChild(divider);
      });
    }
  }

  const livesText = preserved.querySelector("#missionLivesText");
  if (livesText && !String(livesText.textContent || "").trim()) {
    const currentLives = window._gear_currentGame?.score?.missionLives;
    const fallbackLives = score?.startingLives;
    const livesValue = currentLives ?? fallbackLives;
    if (livesValue !== undefined && livesValue !== null) {
      livesText.textContent = String(livesValue);
    }
  }
}

// Create the preserved frame element, applying typography and visual elements to ensure it looks correct when shown in the tutorial board preview
export function createPreservedFrame({ display, html, score }) {
  const preserved = document.createElement("div");
  preserved.id = "gear-preserved-frame";
  preserved.style.position = "fixed";
  preserved.style.top = "0";
  preserved.style.left = "0";
  preserved.style.width = "100%";
  preserved.style.height = "100%";
  preserved.style.zIndex = "9999";
  applyPreservedFrameTypography(preserved, display);
  preserved.innerHTML = html;

  // Remove dynamic HQ panel overlays from frozen snapshots to avoid stale DOM artifacts.
  const hadDynamicHQPanel = !!preserved.querySelector("#hq-message-panel, #hq-lane-choice-panel");
  preserved.querySelectorAll("#hq-message-panel, #hq-lane-choice-panel").forEach((el) => el.remove());

  ensurePreservedFrameVisuals(preserved, score);

  // Hide bike and target car in preserved layer to avoid broken image artifacts.
  const driverInPreserved = preserved.querySelector("#driverCar, #driverImage");
  if (driverInPreserved) driverInPreserved.style.display = "none";

  const targetCarInPreserved = preserved.querySelector("#targetCar");
  if (targetCarInPreserved) targetCarInPreserved.style.display = "none";

  // If the frozen frame came from a dynamic HQ state, hide fallback HQ image to prevent
  // broken-image alt text from appearing behind fail/success overlays.
  const agentImageInPreserved = preserved.querySelector("#agentImage");
  if (agentImageInPreserved) {
    const src = String(agentImageInPreserved.getAttribute("src") || "");
    const looksLikeStaticHQMessageAsset = /(^|\/)HQmessage/i.test(src);
    if (hadDynamicHQPanel || looksLikeStaticHQMessageAsset) {
      agentImageInPreserved.style.display = "none";
    }
  }

  return preserved;
}

// Prepares the game board preview for the tutorial by initializing necessary game components
export function prepareBoardPreview(game, phaseToPreview) {
  try {
    if (!game) return;

    game.initializeGameComponents();
    game.initializeHUD();
    game.updateCoordinates();
    game.moveHighwayDividers(0);

    if (phaseToPreview >= 2 && typeof game.matchDriverAndTargetSpeed === "function") {
      game.matchDriverAndTargetSpeed();
      game.updateCoordinates();
    }

    if (isLaneLabelAgentAsset(game.trialConfig.agentFile)) {
      // Keep board preview aligned with runtime lane-label rendering (dynamic HTML panel).
      game.showAgentImage(game.trialConfig.agentFile);
    }

    // Hide target car — it shouldn't appear in the static board preview shown behind the ready overlay.
    // Use classList (same mechanism as game's hideElement/showElement) so startPhase1's showElement restores it correctly.
    if (game.targetCar) game.targetCar.classList.add("invisible");
  } catch (e) {
    console.warn("prepareBoardPreview: failed to prepare board preview", e);
  }
}
